import { ReactNode, useMemo } from 'react'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import {
  getClass,
  getAllSpecializations,
} from 'app/frontend/base/services/characterValues'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import TeamPlanAssignmentVisualCard, {
  VisualPlanData,
} from '../../TeamPlans/components/TeamPlanAssignmentVisualCard'
import {
  buildVisualEntitySlots,
  resolveVisualPlanEntityAssignments,
} from '../../TeamPlans/components/visual_plans/shapes/entityAssignmentMatching'
import { ResolvedAssignmentParticipant } from './eventActivityTimelineResolver'
import EventActivityLinkedPlanTitleRow from './EventActivityLinkedPlanTitleRow'
import { applyVisualPlanEntityOverrides } from '../hooks/applyVisualPlanEntityOverrides'
import {
  EncounterAssignmentEntityOverride,
  EventPlanOverridesByPlanId,
  EventPlanVisualEntityOverrides,
  clearVisualEntityOverrides,
  getPlanVisualEntityOverrides,
  setVisualEntityOverrides,
  visualEntityOverridesAfterSelect,
  visualEntitySlotKey,
} from './eventActivityPlanOverrides'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import { buildAssignmentPaletteEntities } from '../../TeamPlans/components/assignmentPaletteEntities'
import getAssignmentEntityIdentity from '../../TeamPlans/components/assignmentEntityIdentity'
import { getAssignmentEntityLabel } from '../../TeamPlans/components/assignmentEntityLabels'
import { useTranslation } from 'react-i18next'

const participantRoleToCharacterRole: Record<string, CharacterRole> = {
  tank: 'tank',
  healer: 'heal',
  melee: 'melee',
  ranged: 'ranged',
  dps: 'melee',
  anyone: 'melee',
  non_tank: 'melee',
  anyone_ranged: 'ranged',
  anyone_melee: 'melee',
}

type Props = {
  visualPlan: VisualPlan
  selectedParticipants: ResolvedAssignmentParticipant[]
  planOverrides: EventPlanOverridesByPlanId
  canEdit: boolean
  titlePrefix?: ReactNode
  defaultExpanded?: boolean
  onChange: () => void
  onUnlink: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  canMoveUp?: boolean
  canMoveDown?: boolean
  onPlanOverridesChange: (nextPlanOverrides: EventPlanOverridesByPlanId) => void
}

const EventActivityVisualPlan = ({
  visualPlan,
  selectedParticipants,
  planOverrides,
  canEdit,
  titlePrefix,
  defaultExpanded = true,
  onChange,
  onUnlink,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onPlanOverridesChange,
}: Props) => {
  const { t } = useTranslation()
  const { guild, team } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const canViewBasePlan = hasAccessToContent(
    'member',
    'plans',
    guild,
    team,
  ).hasAccess
  const basePlanPath =
    visualPlan.ownerType === 'User'
      ? `/profile/events/plans/visuals/${visualPlan.id}`
      : `${team.path}/events/plans/visuals/${visualPlan.id}`
  const editPath = canViewBasePlan ? basePlanPath : undefined

  const memberInfoByCharacterId = useMemo(
    () =>
      selectedParticipants.reduce<
        Record<number, { name: string; className: string }>
      >((acc, participant) => {
        acc[participant.characterId] = {
          name: participant.name,
          className: participant.characterClass,
        }
        return acc
      }, {}),
    [selectedParticipants],
  )
  const specializationById = useMemo(
    () =>
      Object.entries(getAllSpecializations()).reduce<
        Record<
          number,
          { name: string; icon: string; classId: number; className: string }
        >
      >((acc, [specId, specialization]) => {
        const classId = specialization.class_id ?? 0
        acc[Number(specId)] = {
          name: specialization.name,
          icon: specialization.icon,
          classId,
          className: getClass(classId.toString()) ?? '',
        }
        return acc
      }, {}),
    [],
  )
  const entities: RaidPlanAssignmentEntity[] = useMemo(
    () =>
      selectedParticipants.map((participant) => ({
        id: `participant-${participant.participantId}`,
        kind: 'member' as const,
        character_id: participant.characterId,
        spec_id: participant.specId,
        role: participantRoleToCharacterRole[participant.role] ?? 'melee',
        label: participant.name,
      })),
    [selectedParticipants],
  )
  const ownCharacterIds = useMemo(
    () =>
      new Set(
        (team.members ?? [])
          .filter((member) => member.characterReference.userId === user?.id)
          .map((member) => member.characterReference.id),
      ),
    [team.members, user?.id],
  )
  const resolvedVisualPlan = useMemo((): VisualPlanData | null => {
    if (visualPlan.screens.length === 0) return null
    const classColorFn = (classId?: number) => {
      const className =
        classId === undefined ? undefined : getClass(classId.toString())
      return className
        ? `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
        : 'var(--color-primary)'
    }
    const paletteLabelByIdentity = new Map(
      buildAssignmentPaletteEntities({
        t,
        members: team.members ?? [],
        specializationById,
        classColor: classColorFn,
      }).map((entry) => [
        getAssignmentEntityIdentity(entry.entity),
        entry.label,
      ]),
    )
    const visualEntityOverrides = getPlanVisualEntityOverrides(
      planOverrides,
      visualPlan.id,
    )
    const screens = visualPlan.screens.map((screen, index) => {
      const slots = buildVisualEntitySlots(screen.shapes)
      const resolved = resolveVisualPlanEntityAssignments({
        shapes: screen.shapes,
        entities,
        specializationById,
        memberInfoByCharacterId,
      })
      const entityAssignments = applyVisualPlanEntityOverrides({
        assignments: resolved,
        slots,
        shapes: screen.shapes,
        screenId: screen.id,
        overrides: visualEntityOverrides,
        entities,
        specializationById,
        memberInfoByCharacterId,
      })
      entityAssignments.forEach((badge) => {
        if (badge.characterId == null) return
        const entity = entities.find(
          (entry) =>
            entry.kind === 'member' && entry.character_id === badge.characterId,
        )
        if (!entity || entity.kind !== 'member') return
        badge.label =
          paletteLabelByIdentity.get(getAssignmentEntityIdentity(entity)) ??
          getAssignmentEntityLabel(t, entity)
        badge.assignedSpecId = entity.spec_id ?? undefined
        badge.assignedClassName =
          (entity.spec_id
            ? specializationById[entity.spec_id]?.className
            : undefined) ||
          memberInfoByCharacterId[entity.character_id]?.className
        if (ownCharacterIds.has(entity.character_id)) {
          badge.isOwn = true
        }
      })
      return {
        key: screen.id,
        screen,
        number: index + 1,
        entityAssignments,
        castTimestamps: [],
      }
    })
    return {
      encounterId: visualPlan.encounterId,
      screens,
    }
  }, [
    entities,
    memberInfoByCharacterId,
    ownCharacterIds,
    planOverrides,
    specializationById,
    t,
    team.members,
    visualPlan.encounterId,
    visualPlan.id,
    visualPlan.screens,
  ])

  const handleOverrideSelect = (
    slotKey: string,
    override: EncounterAssignmentEntityOverride,
  ) => {
    const screenEntry = resolvedVisualPlan?.screens.find((entry) => {
      const slots = buildVisualEntitySlots(entry.screen.shapes)
      return slots.some(
        (slot) =>
          visualEntitySlotKey(
            entry.screen.id,
            slot.shapeId,
            slot.localIndex,
          ) === slotKey,
      )
    })
    const slotAssignments = screenEntry
      ? buildVisualEntitySlots(screenEntry.screen.shapes).map((slot) => {
          const badge = screenEntry.entityAssignments.get(slot.number)
          return {
            slotKey: visualEntitySlotKey(
              screenEntry.screen.id,
              slot.shapeId,
              slot.localIndex,
            ),
            characterId: badge?.characterId,
          }
        })
      : []
    const currentOverrides: EventPlanVisualEntityOverrides =
      getPlanVisualEntityOverrides(planOverrides, visualPlan.id) ?? {}
    const nextVisualEntityOverrides = visualEntityOverridesAfterSelect({
      visualEntityOverrides: currentOverrides,
      slotAssignments,
      clickedSlotKey: slotKey,
      override,
    })
    onPlanOverridesChange(
      setVisualEntityOverrides({
        planOverrides,
        visualPlanId: visualPlan.id,
        visualEntityOverrides: nextVisualEntityOverrides,
      }),
    )
  }

  if (!resolvedVisualPlan) return null

  return (
    <EventActivityLinkedPlanTitleRow
      name={visualPlan.name}
      prefix={titlePrefix}
      editPath={editPath}
      canEdit={canEdit}
      defaultExpanded={defaultExpanded}
      onChange={onChange}
      onUnlink={onUnlink}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
    >
      <div className='mb-3'>
        <TeamPlanAssignmentVisualCard
          label='Visual'
          visualPlan={resolvedVisualPlan}
          visual={null}
          defaultExpanded
          asCard={false}
          hideHeader
          canEditEntities={canEdit}
          selectedParticipants={selectedParticipants}
          onEntityOverrideSelect={canEdit ? handleOverrideSelect : undefined}
          onResetOverrides={
            canEdit
              ? () =>
                  onPlanOverridesChange(
                    clearVisualEntityOverrides({
                      planOverrides,
                      visualPlanId: visualPlan.id,
                    }),
                  )
              : undefined
          }
        />
      </div>
    </EventActivityLinkedPlanTitleRow>
  )
}

export default EventActivityVisualPlan
